import { getLiveStudyExperience } from '@/pages/try/studyExperience/mode';
// Typed fetch with auto-refresh on 401. See auth-system-prd.md §3 + §4.3.

import { surfacePrincipal, useAuthStore } from '@/auth/authStore';
import type { PrincipalKind } from '@/auth/types';
import type { GenAssetRequest, GenAssetResult } from '@/pages/learn/playground/assetGen';
import { rememberAcademyAssetUrls } from '@/lib/academyAssets';
import { airbotixClientHeaders } from '@/lib/authClient';
import { sameHostInDev } from '@/lib/devHost';
import {
  STUDENT_PAYMENT_PERMISSION_REQUIRED,
  requestStudentPaymentPermission,
} from '@/lib/studentPaymentPermissionGate';

// In dev, when the app is opened from a LAN device (phone/tablet), talk to the
// dev machine that served it rather than the device's own localhost.
export const BASE_URL = sameHostInDev(import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000');

// A request inherits the principal of the surface it is fired from unless an
// explicit `principal` is passed (e.g. /auth/me, login/logout target a kind).
const defaultPrincipal = surfacePrincipal;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
    public readonly requestId?: string,
  ) {
    super(message);
  }
}

export interface ApiOpts {
  /** Authenticated developer review on the current Vite origin only. */
  localStudyReview?: boolean;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  skipAuthRefresh?: boolean;
  /** Which session's token to use. Defaults to the current surface's principal. */
  principal?: PrincipalKind;
  headers?: Record<string, string>;
  /**
   * Internal: set on the one repeat after the student confirmed payment
   * permission (studentPaymentPermissionGate.ts), so a second refusal is
   * returned to the page instead of opening the step again.
   */
  paymentPermissionRetried?: boolean;
}

export type DemoApiAdapter = <T>(path: string, opts: ApiOpts) => Promise<T>;

// Public `/try/*` studios install a short-lived, in-memory adapter before the
// real Studio tree mounts. This is the final fail-closed boundary for nested
// controls that still use the shared API client: demo interaction must never
// refresh auth, write cloud state or accidentally start a paid generation.
let demoApiAdapter: DemoApiAdapter | null = null;

export function setDemoApiAdapter(adapter: DemoApiAdapter | null): void {
  demoApiAdapter = adapter;
}

// Optional, explicit bytes boundary for isolated pre-generated studio examples.
// Other Demo downloads remain closed; never fall through to authenticated fetch.
let demoBlobAdapter: ((path: string) => Promise<Blob>) | null = null;
export function setDemoBlobAdapter(adapter: ((path: string) => Promise<Blob>) | null) { demoBlobAdapter = adapter; }

// Distinguishes "the refresh cookie is truly dead" (401/403 → drop the session)
// from a transient refresh failure (429 rate-limit, 5xx, network — e.g. a shared
// venue IP hitting a limit, or a deploy window). Transient failures must NOT
// kill the session: the 2026-07-18 incident mass-logged-out whole venues because
// every refresh failure was treated as session death.
interface RefreshOutcome {
  token: string | null;
  sessionDead: boolean;
}

// One retry after a short pause covers a backend restart blip without turning
// the client into a retry storm.
export const REFRESH_RETRY_DELAY_MS = 1500;

// A refresh that never settles is worse than one that fails: `bootstrapped` only
// flips once the mount-time restore resolves, and the route guards render the
// splash until it does. Bound every attempt. This surface awaits BOTH principal
// kinds together, so one stalled request would strand a parent AND a kid.
// (2026-09-21 incident on the admin console, same defect — nginx logged
// `upstream prematurely closed connection` and the restore never completed.)
export const REFRESH_REQUEST_TIMEOUT_MS = 6000;

// Hard deadline for the mount-time restore, so the splash always clears even if
// a bounded attempt is somehow still outstanding. A late token still applies:
// `refreshSession` calls setToken whenever the request finally resolves.
export const SESSION_BOOTSTRAP_DEADLINE_MS = 8000;

// In-flight dedupe is per kind so a parent and kid refresh can run concurrently
// without clobbering each other.
const refreshInFlight: Record<PrincipalKind, Promise<RefreshOutcome> | null> = {
  user: null,
  kid: null,
};

// The parent Portal and the /teacher/* pages share one app refresh cookie. A
// teacher who also signed in here as a parent holds both roles, and the backend
// picks `parent` unless the page names its surface — then every teacher call
// answers 403 ("We couldn't open this project", 2026-09-24 production incident).
export function refreshQuery(kind: PrincipalKind): string {
  const onTeacherPage =
    typeof window !== 'undefined' && /^\/teacher(\/|$)/.test(window.location.pathname);
  return kind === 'user' && onTeacherPage ? `kind=user&surface=teacher` : `kind=${kind}`;
}

function fetchRefresh(kind: PrincipalKind): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REFRESH_REQUEST_TIMEOUT_MS);
  return fetch(`${BASE_URL}/auth/refresh?${refreshQuery(kind)}`, {
    method: 'POST',
    headers: airbotixClientHeaders(),
    credentials: 'include',
    signal: controller.signal,
  }).finally(() => clearTimeout(timer));
}

function refreshSession(kind: PrincipalKind): Promise<RefreshOutcome> {
  const existing = refreshInFlight[kind];
  if (existing) return existing;
  const run = (async (): Promise<RefreshOutcome> => {
    try {
      let res: Response | null = null;
      try {
        res = await fetchRefresh(kind);
      } catch {
        res = null; // network error — retry below
      }
      if (!res || res.status >= 500) {
        await new Promise((r) => setTimeout(r, REFRESH_RETRY_DELAY_MS));
        try {
          res = await fetchRefresh(kind);
        } catch {
          return { token: null, sessionDead: false };
        }
      }
      if (res.status === 401 || res.status === 403) return { token: null, sessionDead: true };
      if (!res.ok) return { token: null, sessionDead: false }; // 429 / lingering 5xx
      try {
        const body = (await res.json()) as { access_token: string };
        useAuthStore.getState().setToken(kind, body.access_token);
        return { token: body.access_token, sessionDead: false };
      } catch {
        return { token: null, sessionDead: false };
      }
    } finally {
      refreshInFlight[kind] = null;
    }
  })();
  refreshInFlight[kind] = run;
  return run;
}

export async function refreshAccessToken(
  kind: PrincipalKind = defaultPrincipal(),
): Promise<string | null> {
  return (await refreshSession(kind)).token;
}

export async function api<T>(path: string, opts: ApiOpts = {}): Promise<T> {
  if (demoApiAdapter) return demoApiAdapter<T>(path, opts);

  const { method = 'GET', body, signal, skipAuthRefresh } = opts;
  if (
    opts.localStudyReview &&
    (!import.meta.env.DEV || !path.startsWith('/__study-cheatsheet-review/'))
  )
    throw new ApiError(403, 'LOCAL_REVIEW_ONLY', 'Local Study review is unavailable.');
  const origin = opts.localStudyReview ? '' : BASE_URL;
  const principal = opts.principal ?? defaultPrincipal();

  const multipart = typeof FormData !== 'undefined' && body instanceof FormData;
  const exec = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      ...(multipart ? {} : { 'content-type': 'application/json' }),
      ...airbotixClientHeaders(),
      ...opts.headers,
    };
    if (token) headers.authorization = `Bearer ${token}`;
    return fetch(`${origin}${path}`, {
      method,
      headers,
      credentials: 'include',
      signal,
      body: multipart ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  };

  let token = useAuthStore.getState().tokens[principal];
  let res = await exec(token);

  let refresh: RefreshOutcome | null = null;
  if (res.status === 401 && !skipAuthRefresh && !getLiveStudyExperience()) {
    refresh = await refreshSession(principal);
    if (refresh.token) {
      token = refresh.token;
      res = await exec(token);
    }
  }

  if (!res.ok) {
    let code = `HTTP_${res.status}`;
    let message = res.statusText;
    let details: unknown;
    let requestId: string | undefined;
    try {
      const errBody = (await res.json()) as {
        error?: { code: string; message: string; details?: unknown; request_id?: string };
      };
      if (errBody.error) {
        code = errBody.error.code;
        message = errBody.error.message;
        details = errBody.error.details;
        requestId = errBody.error.request_id;
      }
    } catch {
      // ignore
    }
    // D2 (student-self-signup-prd.md §19): an independent student who has not
    // yet confirmed they may pay. The server refused before anything was
    // bought, so after the student confirms, send the same request once more.
    if (
      res.status === 403 &&
      code === STUDENT_PAYMENT_PERMISSION_REQUIRED &&
      !opts.paymentPermissionRetried &&
      (await requestStudentPaymentPermission())
    ) {
      return api<T>(path, { ...opts, paymentPermissionRetried: true });
    }
    if (res.status === 401) {
      // Only drop the failing principal's session — the other one survives.
      // And only when the session is actually dead: the refresh cookie was
      // rejected (401/403), or a freshly-minted access token still got 401.
      // A refresh that merely couldn't run (429 / 5xx / offline) keeps the
      // session — the next request will try again.
      const refreshWasTransient =
        refresh !== null && !refresh.sessionDead && refresh.token === null;
      if (!refreshWasTransient) {
        useAuthStore.getState().clearToken(principal);
      }
    }
    throw new ApiError(res.status, code, message, details, requestId);
  }

  if (res.status === 204) return undefined as T;
  const payload = (await res.json()) as T;
  // SIR-HYG-03: question responses carry signed figure URLs; remember them for <img> tags.
  rememberAcademyAssetUrls(payload);
  return payload;
}

export type ApiFormOpts = Omit<ApiOpts, 'body' | 'method'> & {
  method?: 'POST' | 'PUT' | 'PATCH';
};

/**
 * Authenticated multipart upload. Sends `formData` through `api` so it keeps the
 * same bearer token, client headers, credentials, one silent 401 refresh and
 * `ApiError` mapping. No content-type is set: the browser writes the multipart
 * boundary. The same FormData is sent again after a refresh.
 */
export function apiForm<T>(path: string, formData: FormData, opts: ApiFormOpts = {}): Promise<T> {
  return api<T>(path, { ...opts, method: opts.method ?? 'POST', body: formData });
}

/**
 * Authenticated same-origin binary fetch → Blob. Mirrors `api`'s auth +
 * silent-refresh handling but returns the raw bytes (no JSON parse). Use it to pull
 * private media (e.g. a class/course asset's bytes) from a backend proxy on the app
 * origin — that keeps the signed S3 URL server-side and avoids the cross-origin S3
 * CORS a direct `fetch()` of the signed URL would need.
 */
export async function apiBlob(
  path: string,
  principal: PrincipalKind = defaultPrincipal(),
): Promise<Blob> {
  if (demoApiAdapter) {
    if (demoBlobAdapter) return demoBlobAdapter(path);
    throw new ApiError(403, 'DEMO_LOCAL_ONLY', 'Downloads are unavailable in this local demo.');
  }

  const exec = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = airbotixClientHeaders();
    if (token) headers.authorization = `Bearer ${token}`;
    return fetch(`${BASE_URL}${path}`, { headers, credentials: 'include' });
  };

  let token = useAuthStore.getState().tokens[principal];
  let res = await exec(token);
  if (res.status === 401 && !getLiveStudyExperience()) {
    const refresh = await refreshSession(principal);
    if (refresh.token) {
      token = refresh.token;
      res = await exec(token);
    } else if (refresh.sessionDead) {
      // Transient refresh failures (429/5xx/offline) keep the session — see api().
      useAuthStore.getState().clearToken(principal);
    }
  }

  if (!res.ok) {
    let code = `HTTP_${res.status}`;
    let message = res.statusText;
    try {
      const errBody = (await res.json()) as { error?: { code: string; message: string } };
      if (errBody.error) {
        code = errBody.error.code;
        message = errBody.error.message;
      }
    } catch {
      // body wasn't JSON — keep the status-based defaults
    }
    throw new ApiError(res.status, code, message);
  }

  return res.blob();
}

/**
 * Authenticated file download (non-JSON, e.g. CSV export). Fetches the bytes with
 * `apiBlob` (auth + silent-refresh) and triggers a browser download.
 */
export async function apiDownload(
  path: string,
  filename: string,
  principal: PrincipalKind = defaultPrincipal(),
): Promise<void> {
  const blob = await apiBlob(path, principal);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Generate a game asset via platform-backend (Stars-metered, audited). The kid
 * surface never calls an LLM directly — this is the real target for the
 * `runGen` seam in `@/pages/learn/playground/assetGen`.
 */
export async function generateAsset(
  req: GenAssetRequest,
  signal?: AbortSignal,
): Promise<GenAssetResult> {
  // The backend DTO (`GenerateAssetSchema`) is snake_case — map the camelCase seam.
  const body = {
    project_id: req.projectId,
    request_id: req.requestId,
    quoted_stars: req.quotedStars,
    kind: req.kind,
    prompt: req.prompt,
    ref_asset_path: req.refAssetPath,
    ref_url: req.refUrl,
    size: req.size,
  };
  return api<GenAssetResult>(req.requestId ? '/llm/student-image' : '/llm/generate-asset', { method: 'POST', body, signal });
}
